import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { Key, Save, AlertCircle, CheckCircle2 } from 'lucide-react';

const LeadScraperSettings = () => {
  const [apiKey, setApiKey] = useState('');
  const [status, setStatus] = useState({ loading: true, hasKey: false, managedBySystem: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api('/lead-scrape/settings', { method: 'GET' });
      const data = await res.json();
      
      if (res.ok) {
        setStatus({ 
          loading: false, 
          hasKey: data.hasKey, 
          managedBySystem: data.managedBySystem 
        });
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (err) {
      console.error(err);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });
    
    try {
      const res = await api('/lead-scrape/settings', {
        method: 'PUT',
        body: { apifyApiToken: apiKey }
      });
      
      if (res.ok) {
        setMessage({ text: 'API Key saved successfully!', type: 'success' });
        setApiKey(''); // Clear input for security
        fetchSettings(); // Refresh status
      } else {
        throw new Error('Failed to save API key');
      }
    } catch (err) {
      setMessage({ text: 'Failed to save API key.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (status.loading) return <div style={{ padding: '20px' }}>Loading settings...</div>;

  // Phase 2: System manages the key
  if (status.managedBySystem) {
    return (
      <div style={{
        background: 'var(--color-background-primary)',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid var(--color-border-tertiary)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: '#E8F5E9', padding: '10px', borderRadius: '8px', color: '#2E7D32' }}>
            <CheckCircle2 size={24} />
          </div>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '18px', color: 'var(--color-text-primary)' }}>Lead Scraper is Active</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              The system is currently managing the Apify API integration globally. You can scrape leads freely!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Phase 1: User provides the key
  return (
    <div style={{
      background: 'var(--color-background-primary)',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid var(--color-border-tertiary)',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <div style={{ background: 'var(--color-background-info)', padding: '10px', borderRadius: '8px', color: 'var(--color-brand-primary)' }}>
          <Key size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '18px', color: 'var(--color-text-primary)' }}>Apify Settings</h3>
            {status.hasKey ? (
              <span style={{ fontSize: '12px', padding: '4px 8px', background: '#E8F5E9', color: '#2E7D32', borderRadius: '12px', fontWeight: '600' }}>
                Key Saved
              </span>
            ) : (
              <span style={{ fontSize: '12px', padding: '4px 8px', background: '#FFEBEE', color: '#C62828', borderRadius: '12px', fontWeight: '600' }}>
                Setup Required
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
            Enter your Apify API Token to enable Google Maps Lead Scraping.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={status.hasKey ? "•••••••••••••••••••• (Enter new key to update)" : "Enter your Apify API Token (e.g. apify_api_...)"}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid var(--color-border-tertiary)',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'var(--color-background-secondary)',
              color: 'var(--color-text-primary)'
            }}
          />
          {message.text && (
            <div style={{ 
              marginTop: '8px', 
              fontSize: '13px', 
              color: message.type === 'success' ? '#2E7D32' : '#C62828',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {message.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
              {message.text}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={saving || !apiKey}
          style={{
            padding: '12px 24px',
            background: 'var(--color-brand-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: (saving || !apiKey) ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            opacity: (saving || !apiKey) ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'opacity 0.2s'
          }}
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Key'}
        </button>
      </form>
    </div>
  );
};

export default LeadScraperSettings;
